import type {ValidatorResult} from "../types";
import {BaseValidator} from "./validator.interface";

export interface ZipCodeBounds {
    min: number;
    max: number;
}

/**
 * Normalizes zip/postal code for length validation by removing spaces and hyphens.
 * Per UPU POST*CODE DataBase: many formats use space or hyphen (e.g. Canada "A9A 9A9",
 * USA "99999-9999"). We validate the significant character count only.
 */
export function normalizeZipCodeForValidation(value: string): string {
    return value.replace(/[\s\-]/g, "");
}

/**
 * Validates zip/postal code length against min/max from a getter (e.g. by selected country).
 * Input is normalized (spaces and hyphens removed) before length check, per UPU rules.
 */
export class ZipCodeLengthByCountryValidator extends BaseValidator {
    private readonly getBounds: () => ZipCodeBounds;

    constructor(getBounds: () => ZipCodeBounds) {
        super("lengthRange");
        this.getBounds = getBounds;
    }

    validate(value: string): ValidatorResult {
        if (!value || value.length === 0) {
            return this.success();
        }

        const normalized = normalizeZipCodeForValidation(value);
        const {min, max} = this.getBounds();
        const length = normalized.length;

        if (length >= min && length <= max) {
            return this.success();
        }
        if (min === max) {
            return this.fail("lengthExact", {count: min});
        }
        return this.fail(undefined, {min, max});
    }
}
